import type { ReactNode } from 'react'
import { NavLink } from 'react-router'
import { cn } from '@/lib/cn'
import { Icon } from '@/components/primitives/Icon'
import { navItems } from './nav'

/** App chrome: a vertical nav rail on tablet/desktop, a bottom bar on phones. */
export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="kiosk flex h-full w-full bg-[var(--color-bg)] text-[var(--color-text)]">
      {/* rail — md and up */}
      <nav className="hidden shrink-0 flex-col items-stretch gap-1 border-r border-[var(--color-hairline)] px-3 py-5 md:flex md:w-[116px]">
        <div className="mb-4 px-2 text-center text-[11px] font-semibold tracking-widest text-[var(--color-faint)]">
          TAUGATA&nbsp;24
        </div>
        {navItems.map((item) => (
          <RailLink key={item.to} to={item.to} label={item.label} icon={item.icon} />
        ))}
      </nav>

      {/* content */}
      <main className="relative h-full flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1600px] px-4 pb-28 pt-5 md:px-8 md:pb-10 md:pt-8">{children}</div>
      </main>

      {/* bottom bar — phones */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-[var(--color-hairline)] bg-[var(--color-surface)]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {navItems
          .filter((i) => i.primary)
          .map((item) => (
            <BarLink key={item.to} to={item.to} label={item.label} icon={item.icon} />
          ))}
      </nav>
    </div>
  )
}

function RailLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'flex flex-col items-center gap-1.5 rounded-[var(--radius-control)] py-3 text-[11px] font-medium transition-colors',
          isActive
            ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
            : 'text-[var(--color-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]',
        )
      }
    >
      <Icon name={icon} size={24} />
      {label}
    </NavLink>
  )
}

function BarLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium',
          isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-muted)]',
        )
      }
    >
      <Icon name={icon} size={22} />
      {label}
    </NavLink>
  )
}
